import { useMemo } from 'react';

import type { Status, Transition, Workflow } from '@tasks/contracts';

import styles from './WorkflowSettingsPage.module.css';

export type TransitionMatrixProps = {
  readonly workflow: Workflow;
  readonly onToggle: (from: Status, to: Status, existing: Transition | null) => void;
  readonly onCondition: (
    transition: Transition,
    patch: { readonly onlyAssignee?: boolean; readonly requireResolution?: boolean },
  ) => void;
};

/** Матрица «статус × статус» с условиями переходов (UC-PRJ-07). */
export function TransitionMatrix({ workflow, onToggle, onCondition }: TransitionMatrixProps) {
  const byPair = useMemo(
    () =>
      new Map(
        workflow.transitions.map((transition) => [
          `${transition.fromStatusId}:${transition.toStatusId}`,
          transition,
        ]),
      ),
    [workflow.transitions],
  );

  return (
    <div className={styles['matrixScroll']}>
      <table className={styles['matrix']}>
        <caption className="u-visually-hidden">Допустимые переходы между статусами</caption>
        <thead>
          <tr>
            <th scope="col">Из \ В</th>
            {workflow.statuses.map((status) => (
              <th key={status.id} scope="col">
                {status.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {workflow.statuses.map((from) => (
            <tr key={from.id}>
              <th scope="row">{from.name}</th>
              {workflow.statuses.map((to) => {
                if (from.id === to.id) {
                  return (
                    <td key={to.id}>
                      <span className="u-muted">—</span>
                    </td>
                  );
                }
                const existing = byPair.get(`${from.id}:${to.id}`) ?? null;
                return (
                  <td key={to.id}>
                    <span className={styles['cellInner']}>
                      <input
                        type="checkbox"
                        checked={existing !== null}
                        aria-label={`Переход ${from.name} → ${to.name}`}
                        onChange={() => onToggle(from, to, existing)}
                      />
                      {existing ? (
                        <span className={styles['conditions']}>
                          <label className={styles['condition']} title="Только исполнитель">
                            <input
                              type="checkbox"
                              checked={existing.onlyAssignee}
                              onChange={(event) => onCondition(existing, { onlyAssignee: event.target.checked })}
                            />
                            исп.
                          </label>
                          <label className={styles['condition']} title="Требуется решение">
                            <input
                              type="checkbox"
                              checked={existing.requireResolution}
                              onChange={(event) =>
                                onCondition(existing, { requireResolution: event.target.checked })
                              }
                            />
                            рез.
                          </label>
                        </span>
                      ) : null}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
